import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSelector, useDispatch } from 'react-redux'
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { db, storage } from '../plugins/firebase'
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage'
import { selectUserUid } from '../store/slices/userSlice'
import { addNotification } from '../store/slices/notificationsSlice'
import { notesValidator } from '../utils/generalUtils'

/**
 * Recursively removes undefined values from an object to prevent Firestore errors
 * @param {any} obj - The object to clean
 * @returns {any} - The cleaned object without undefined values
 */
const removeUndefinedValues = (obj) => {
  if (obj === null || typeof obj !== 'object') {
    return obj === undefined ? null : obj
  }
  
  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedValues).filter(item => item !== undefined)
  }
  
  const cleaned = {}
  Object.keys(obj).forEach(key => {
    const value = removeUndefinedValues(obj[key])
    if (value !== undefined) {
      cleaned[key] = value
    }
  })
  
  return cleaned
}

// Query keys
export const queryKeys = {
  entries: (userId) => ['entries', userId],
  entry: (userId, entryId) => ['entries', userId, entryId],
}

// Custom hook for entries with real-time updates
export const useEntriesQuery = () => {
  const userId = useSelector(selectUserUid)
  const dispatch = useDispatch()
  
  return useQuery({
    queryKey: queryKeys.entries(userId),
    queryFn: () => {
      return new Promise((resolve, reject) => {
        if (!userId) {
          resolve([])
          return
        }

        const entriesCollection = collection(db, 'flocks', userId, 'entries')
        const entries = []
        
        const unsubscribe = onSnapshot(
          entriesCollection,
          (querySnapshot) => {
            const updatedEntries = []
            
            querySnapshot.forEach((doc) => {
              const entry = {
                entryId: doc.id,
                ...doc.data(),
                notes: notesValidator(doc.data().notes),
              }
              updatedEntries.push(entry)
            })
            
            // For initial load
            if (entries.length === 0) {
              entries.push(...updatedEntries)
              resolve(updatedEntries)
            }
            
            // Handle real-time updates
            querySnapshot.docChanges().forEach((change) => {
              const entry = {
                entryId: change.doc.id,
                ...change.doc.data(),
                notes: notesValidator(change.doc.data().notes),
              }

              switch (change.type) {
                case 'added':
                  // Removed notification for found entries
                  break
                case 'modified':
                  dispatch(addNotification({ type: 'update', entryId: entry.entryId }))
                  break
                case 'removed':
                  dispatch(addNotification({ type: 'removed', entryId: entry.entryId }))
                  break
              }
            })
          },
          (error) => {
            dispatch(addNotification({ type: 'error' }))
            reject(error)
          }
        )
        
        // Cleanup function will be handled by React Query
        return () => unsubscribe()
      })
    },
    enabled: !!userId,
    staleTime: Infinity, // Real-time data, never stale
  })
}

// Entry CRUD mutations
export const useCreateEntryMutation = () => {
  const userId = useSelector(selectUserUid)
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ formData, attachments = [] }) => {
      if (!userId) throw new Error('User not authenticated')

      if (attachments.length > 0) {
        formData.photoIds = [crypto.randomUUID()]
      }

      const entriesCollectionRef = collection(db, 'flocks', userId, 'entries')
      
      // Ensure breed is always an array
      if (formData.breed && typeof formData.breed === 'string') {
        formData.breed = [formData.breed]
      }
      
      // Clean the form data to remove undefined values that would cause Firestore errors
      const cleanedFormData = removeUndefinedValues(formData)
      
      const { id: entryId } = await addDoc(entriesCollectionRef, {
        ...cleanedFormData,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      })

      if (attachments.length > 0) {
        await uploadImages(userId, entryId, formData.photoIds[0], attachments)
      }

      return entryId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entries(userId) })
    }
  })
}

export const useUpdateEntryMutation = () => {
  const userId = useSelector(selectUserUid)
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ entryId, entryData, userDisplayName, userPhotoURL }) => {
      if (!userId) throw new Error('User not authenticated')
      
      const timestamp = Timestamp.now()
      const copy = { ...entryData }
      
      // Ensure breed is always an array
      if (copy.breed && typeof copy.breed === 'string') {
        copy.breed = [copy.breed]
      }
      
      // Handle notes
      if (copy.notes.active?.length > 0) {
        copy.notes.archived.push({
          timestamp,
          content: copy.notes.active,
          user: userDisplayName,
          userPhotoURL: userPhotoURL || 'https://randomuser.me/api/portraits/lego/1.jpg'
        })
        copy.notes.active = ''
      }

      const entryRef = doc(db, 'flocks', userId, 'entries', entryId)
      
      // Clean the entry data to remove undefined values that would cause Firestore errors
      const cleanedCopy = removeUndefinedValues(copy)
      
      await updateDoc(entryRef, {
        ...cleanedCopy,
        updatedAt: timestamp
      })

      return { entryId, updatedEntry: copy }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entries(userId) })
    }
  })
}

export const useDeleteEntryMutation = () => {
  const userId = useSelector(selectUserUid)
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (entryId) => {
      if (!userId) throw new Error('User not authenticated')
      
      const entryRef = doc(db, 'flocks', userId, 'entries', entryId)
      await deleteDoc(entryRef)
      return entryId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entries(userId) })
    }
  })
}

export const useToggleFoundationMutation = () => {
  const userId = useSelector(selectUserUid)
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ entryId, isFoundation }) => {
      if (!userId) throw new Error('User not authenticated')
      
      const entryRef = doc(db, 'flocks', userId, 'entries', entryId)
      await updateDoc(entryRef, {
        isFoundation: !isFoundation,
        updatedAt: Timestamp.now()
      })
      return { entryId, isFoundation: !isFoundation }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entries(userId) })
    }
  })
}

export const useToggleFavoriteMutation = () => {
  const userId = useSelector(selectUserUid)
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ entryId, isFavorite }) => {
      if (!userId) throw new Error('User not authenticated')
      
      const entryRef = doc(db, 'flocks', userId, 'entries', entryId)
      await updateDoc(entryRef, {
        isFavorited: !isFavorite,
        updatedAt: Timestamp.now()
      })
      return { entryId, isFavorited: !isFavorite }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entries(userId) })
    }
  })
}

// Helper function for uploading images
const uploadImages = async (flockId, entryId, uniqueId, attachments) => {
  const refPath = storageRef(storage, `${flockId}/${entryId}/${uniqueId}.jpg`)
  await uploadBytes(refPath, attachments[0])
}

// Helper function for getting image URLs
export const useEntryImageUrl = (entry) => {
  const userId = useSelector(selectUserUid)
  
  return useQuery({
    queryKey: ['entryImage', userId, entry?.entryId, entry?.defaultImage],
    queryFn: async () => {
      if (!entry?.defaultImage || !userId) {
        return 'https://cdn.vuetifyjs.com/images/cards/docks.jpg'
      }

      try {
        const refPath = storageRef(
          storage,
          `${userId}/${entry.entryId}/${entry.defaultImage}`
        )
        return await getDownloadURL(refPath)
      } catch (error) {
        // Only log non-404 errors to reduce noise from missing images
        if (error.code !== 'storage/object-not-found') {
          console.error('Error fetching image URL:', error)
        }
        return 'https://cdn.vuetifyjs.com/images/cards/docks.jpg'
      }
    },
    enabled: !!entry?.entryId,
    staleTime: 1000 * 60 * 30, // 30 minutes
  })
}